import React, { useEffect, useRef, useState } from 'react';

import {
  andar,
  atalhos,
  emMesesInteiros,
  escolher,
  faixaDoPeriodo,
  IPeriodo,
  IRascunho,
  limitar,
  MESES_CURTOS,
  MESES_LONGOS,
  PERIODICIDADES,
  rotuloDoPeriodo,
  trocarTipo,
} from '../../regrasDoPeriodoDaEmpresa';

const SEM_QUINZENA = 'Por enquanto, a quinzena só está disponível em Demonstrações';

/**
 * Botão "Período" do cabeçalho da empresa com o painel do layout de 10/2026:
 * periodicidade, anos (um ou vários), grade de meses ou de quinzenas e atalhos; ‹ ›
 * andam um bloco. Fora de Demonstrações o período vale em meses inteiros e a quinzena
 * fica indisponível, sem perder o que foi escolhido.
 */
export function SeletorDoPeriodo({
  periodo,
  ultimos,
  comQuinzena,
  onAplicar,
}: {
  periodo: IPeriodo;
  /** último mês (0 a 11) com revisão em cada ano */
  ultimos: Record<number, number>;
  comQuinzena: boolean;
  onAplicar: (p: IPeriodo) => void;
}) {
  const visto = comQuinzena ? periodo : emMesesInteiros(periodo);
  const [aberto, setAberto] = useState(false);
  const [r, setR] = useState<IRascunho>({ ...visto, esperandoFim: false });
  const caixa = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!aberto) return undefined;
    const fora = (e: MouseEvent) => {
      if (caixa.current && !caixa.current.contains(e.target as Node)) setAberto(false);
    };
    const esc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setAberto(false);
    };
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', fora);
      document.removeEventListener('keydown', esc);
    };
  }, [aberto]);

  const anos = Object.keys(ultimos)
    .map(Number)
    .filter(a => ultimos[a] >= 0)
    .sort((a, b) => b - a);
  const ultimoSel = Math.max(-1, ...r.anos.map(a => ultimos[a] ?? -1));
  const anterior = andar(visto, -1, ultimos);
  const proximo = andar(visto, 1, ultimos);
  const aplicar = (p: IPeriodo) => {
    setAberto(false);
    onAplicar(limitar(p, ultimos));
  };
  const quinzenal = r.tipo === 'quinzenal';
  const faixa = [faixaDoPeriodo(r), r.esperandoFim ? 'clique no fim do intervalo' : '']
    .filter(Boolean)
    .join(' · ');

  return (
    <div className="perWrap" ref={caixa}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button
          type="button"
          className="perBtn"
          aria-expanded={aberto}
          aria-haspopup="dialog"
          onClick={() => {
            if (!aberto) setR({ ...visto, esperandoFim: false });
            setAberto(!aberto);
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#FFFFFF"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <rect x="3" y="5" width="18" height="16" rx="2" />
            <path d="M16 3v4M8 3v4M3 10h18" />
          </svg>
          Período {rotuloDoPeriodo(visto)}
        </button>
        <button
          type="button"
          className="perSeta"
          aria-label="Período anterior"
          disabled={!anterior}
          onClick={() => anterior && onAplicar(anterior)}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#1B6FD0"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        <button
          type="button"
          className="perSeta"
          aria-label="Próximo período"
          disabled={!proximo}
          onClick={() => proximo && onAplicar(proximo)}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#1B6FD0"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>
      {aberto && (
        <div className="perPop" role="dialog" aria-label="Escolher período">
          <div className="perCorpo">
            <div role="radiogroup" aria-label="Periodicidade">
              <span className="perTit">Periodicidade</span>
              {PERIODICIDADES.map(([k, nome]) => {
                const indisp = k === 'quinzenal' && !comQuinzena;
                return (
                  <button
                    key={k}
                    type="button"
                    role="radio"
                    aria-checked={r.tipo === k}
                    className={`perTipo ${r.tipo === k ? 'on' : ''}`}
                    disabled={indisp}
                    title={indisp ? SEM_QUINZENA : undefined}
                    onClick={() => setR(trocarTipo(r, k, ultimoSel))}
                  >
                    <span className="perRadio" />
                    {nome}
                  </button>
                );
              })}
            </div>
            <div>
              <div className="perAnos" role="group" aria-label="Anos">
                <span className="perTit" style={{ margin: 0 }}>
                  Anos
                </span>
                {anos.map(ano => {
                  const ativo = r.anos.includes(ano);
                  return (
                    <button
                      key={ano}
                      type="button"
                      className={`perAno ${ativo ? 'on' : ''}`}
                      aria-pressed={ativo}
                      onClick={() => {
                        // sempre fica pelo menos um ano marcado
                        if (ativo && r.anos.length === 1) return;
                        const novos = ativo ? r.anos.filter(a => a !== ano) : [...r.anos, ano];
                        setR({ ...r, anos: novos.sort((a, b) => a - b) });
                      }}
                    >
                      {ano}
                    </button>
                  );
                })}
              </div>
              {quinzenal ? (
                <div className="perGrade perGradeQ">
                  {Array.from({ length: 24 }, (_, h) => {
                    const indisp = Math.floor(h / 2) > ultimoSel;
                    const dentro = !indisp && h >= r.h1 && h <= r.h2;
                    return (
                      <button
                        key={h}
                        type="button"
                        className={`perMes ${indisp ? 'off' : ''} ${dentro ? 'sel' : ''}`}
                        disabled={indisp}
                        aria-pressed={dentro}
                        onClick={() => setR(escolher(r, h, h, ultimoSel))}
                      >
                        {h % 2 ? '2ª ' : '1ª '}
                        {MESES_CURTOS[Math.floor(h / 2)]}
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="perGrade">
                  {MESES_LONGOS.map((nome, i) => {
                    const indisp = i > ultimoSel;
                    const dentro = !indisp && i * 2 + 1 >= r.h1 && i * 2 <= r.h2;
                    return (
                      <button
                        key={nome}
                        type="button"
                        className={`perMes ${indisp ? 'off' : ''} ${dentro ? 'sel' : ''}`}
                        disabled={indisp}
                        aria-pressed={dentro}
                        onClick={() => setR(escolher(r, i * 2, i * 2 + 1, ultimoSel))}
                      >
                        {nome}
                      </button>
                    );
                  })}
                </div>
              )}
              <span className="perFaixa">{faixa}</span>
            </div>
          </div>
          <div className="perAtalhos">
            {atalhos(ultimos).map(([nome, p]) => {
              const indisp = p.tipo === 'quinzenal' && !comQuinzena;
              return (
                <button
                  key={nome}
                  type="button"
                  className="perAtalho"
                  disabled={indisp}
                  title={indisp ? SEM_QUINZENA : undefined}
                  onClick={() => setR({ ...limitar(p, ultimos), esperandoFim: false })}
                >
                  {nome}
                </button>
              );
            })}
          </div>
          <div className="perRodape">
            <button
              type="button"
              className="perAplicar"
              onClick={() => aplicar({ anos: r.anos, h1: r.h1, h2: r.h2, tipo: r.tipo })}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#FFFFFF"
                strokeWidth="2.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
              Aplicar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
